import React, { useEffect, useState, useCallback } from "react";
import { api } from "../api";
import ScheduleModal from "./ScheduleModal";
import "./css/schedules.css";

const DAY_LABELS = { mon:"Mon", tue:"Tue", wed:"Wed", thu:"Thu", fri:"Fri", sat:"Sat", sun:"Sun" };

function formatDays(days) {
  if (!days || days.length === 0) return "—";
  if (days.length === 7) return "Every Day";
  return days.map(d => DAY_LABELS[d] || d).join(", ");
}

function formatTime(time) {
  if (!time) return "—";
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function ScheduleCard({ schedule, onEdit, onDelete, onToggle }) {
  const [toggling, setToggling] = useState(false);
  const isMaster = schedule.schedule_type !== 'individual';

  async function handleToggle() {
    setToggling(true);
    await onToggle(schedule);
    setToggling(false);
  }

  return (
    <div className={`schedule-card${!schedule.enabled ? " disabled" : ""}`}>
      <div className="schedule-card-info">
        <div className="schedule-card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {schedule.name || `${schedule.device_name} — ${schedule.action}`}
          {isMaster && (
            <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: "rgba(200,168,75,0.15)", color: "#c8a84b", fontWeight: 700, textTransform: "uppercase" }}>
              Master
            </span>
          )}
        </div>
        <div className="schedule-card-meta">
          <span>{schedule.device_name}</span>
          <span><span className={`schedule-badge ${schedule.action}`}>{schedule.action.toUpperCase()}</span></span>
          <span>{formatTime(schedule.time)}</span>
          <span>{formatDays(schedule.days)}</span>
          {schedule.skip_holidays && <span style={{ color: "#c8a84b" }}>Skips holidays</span>}
          {schedule.skip_weekends && <span style={{ color: "#c8a84b" }}>Skips weekends</span>}
        </div>
      </div>
      <div className="schedule-card-actions">
        <label className="toggle-switch">
          <input type="checkbox" checked={!!schedule.enabled} onChange={handleToggle} disabled={toggling} />
          <span className="toggle-slider" />
        </label>
        {!isMaster && <button className="btn-icon" onClick={() => onEdit(schedule)}>Edit</button>}
        <button className="btn-icon danger" onClick={() => onDelete(schedule.id)}>Delete</button>
      </div>
    </div>
  );
}

function HolidayManager() {
  const [holidays, setHolidays] = useState([]);
  const [date, setDate]         = useState("");
  const [name, setName]         = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.getHolidays().then(setHolidays).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleAdd() {
    setError("");
    if (!date) return setError("Select a date.");
    try {
      const h = await api.addHoliday({ date, name });
      setHolidays(prev => [...prev, h].sort((a,b) => a.date.localeCompare(b.date)));
      setDate(""); setName("");
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id) {
    await api.deleteHoliday(id);
    setHolidays(prev => prev.filter(h => h.id !== id));
  }

  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ color: "#c8a84b", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 12px" }}>
        Holidays (lights stay OFF all day)
      </h3>

      {error && <div className="schedules-error" style={{ marginBottom: 10 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input
          type="date"
          className="modal-input"
          style={{ width: "auto", flex: 1, minWidth: 140 }}
          value={date}
          onChange={e => setDate(e.target.value)}
        />
        <input
          type="text"
          className="modal-input"
          style={{ width: "auto", flex: 2, minWidth: 160 }}
          placeholder="Holiday name (optional)"
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <button className="btn-add-schedule" style={{ padding: "9px 14px" }} onClick={handleAdd}>
          Add
        </button>
      </div>

      {loading && <p className="schedules-loading">Loading…</p>}
      {!loading && holidays.length === 0 && (
        <p style={{ color: "#444", fontSize: 13 }}>No holidays added yet.</p>
      )}
      {holidays.map(h => (
        <div key={h.id} style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "#181818", border: "1px solid #2c2c2c", borderRadius: 8,
          padding: "10px 14px", marginBottom: 8,
        }}>
          <div>
            <span style={{ fontSize: 13, color: "#eaeaea", fontWeight: 500 }}>{h.name || "Holiday"}</span>
            <span style={{ fontSize: 12, color: "#666", marginLeft: 12 }}>{h.date}</span>
          </div>
          <button className="btn-icon danger" onClick={() => handleDelete(h.id)}>Remove</button>
        </div>
      ))}
    </div>
  );
}

export default function SchedulesPage() {
  const [schedules, setSchedules]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [settingUp, setSettingUp]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setSchedules(await api.getSchedules()); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSetupMaster() {
    setSettingUp(true);
    try {
      const result = await api.setupMaster();
      await load();
      alert(`Created ${result.schedules_created} master schedule(s) and ${result.holidays_created} holiday(s).`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSettingUp(false);
    }
  }

  async function handleSave(payload, id) {
    if (id) await api.updateSchedule(id, payload);
    else    await api.createSchedule(payload);
    await load();
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this schedule?")) return;
    await api.deleteSchedule(id);
    setSchedules(prev => prev.filter(s => s.id !== id));
  }

  async function handleToggle(schedule) {
    const updated = schedule.enabled
      ? await api.disableSchedule(schedule.id)
      : await api.enableSchedule(schedule.id);
    setSchedules(prev => prev.map(s => s.id === updated.id ? updated : s));
  }

  const masterSchedules     = schedules.filter(s => s.schedule_type !== 'individual');
  const individualSchedules = schedules.filter(s => s.schedule_type === 'individual');

  return (
    <div className="schedules-page">
      <div className="schedules-page-header">
        <h2>Schedules</h2>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn-add-schedule"
            style={{ background: "transparent", border: "1px solid #c8a84b", color: "#c8a84b" }}
            onClick={handleSetupMaster}
            disabled={settingUp}
          >
            {settingUp ? "Setting up…" : "Setup Master Schedules"}
          </button>
          <button className="btn-add-schedule" onClick={() => { setEditing(null); setShowModal(true); }}>
            + Add Schedule
          </button>
        </div>
      </div>

      {error && <div className="schedules-error">{error}</div>}
      {loading && <div className="schedules-loading">Loading schedules…</div>}

      {/* Master schedules */}
      {masterSchedules.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ color: "#c8a84b", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 10px" }}>
            Master Schedules
          </h3>
          {masterSchedules.map(s => (
            <ScheduleCard key={s.id} schedule={s} onEdit={setEditing} onDelete={handleDelete} onToggle={handleToggle} />
          ))}
        </div>
      )}

      {/* Individual schedules */}
      {individualSchedules.length > 0 && (
        <div>
          <h3 style={{ color: "#888", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 10px" }}>
            Individual Schedules
          </h3>
          {individualSchedules.map(s => (
            <ScheduleCard key={s.id} schedule={s} onEdit={s2 => { setEditing(s2); setShowModal(true); }} onDelete={handleDelete} onToggle={handleToggle} />
          ))}
        </div>
      )}

      {!loading && schedules.length === 0 && (
        <div className="schedules-empty">
          <p>No schedules yet.</p>
          <p>Click <strong>Setup Master Schedules</strong> to create the default ON/OFF schedule for all lights.</p>
        </div>
      )}

      <HolidayManager />

      {showModal && (
        <ScheduleModal
          schedule={editing}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
